/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiSpacer, EuiText, EuiBadge } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import { getSliderPrettyName } from '@/lib/sliders/messages'
import prettySize from '@/lib/prettySize'
import { getSizeOptionText } from '@/components/Topology/DeploymentTemplates/components/DeploymentInfrastructure/TopologyElement/helpers'
import { isDedicatedML } from '@/lib/stackDeployments/selectors/nodeRoles'

import type { FunctionComponent } from 'react'
import type { HardwareSummaryProps as Props, ZoneSummaryInstanceDiff } from './types'

const HardwareSummary: FunctionComponent<Props> = ({
  instance: {
    name,
    icConfigVersion,
    sliderInstanceType,
    sliderNodeType: sliderNodeTypes,
    storageMultiplier,
    cpuMultiplier,
    totalSize,
  },
  diffs,
  topologyElement,
  showDetails = false,
  moreSpace = false,
}): JSX.Element => (
  <Fragment>
    {!showDetails && (
      <EuiText size='s'>
        {name} {` `}
        {icConfigVersion !== undefined ? <EuiBadge>v{icConfigVersion}</EuiBadge> : ``}
      </EuiText>
    )}
    {moreSpace ? <EuiSpacer size='l' /> : <EuiSpacer size='xs' />}
    {sliderNodeTypes && sliderNodeTypes.length > 0 && (
      <Fragment>
        <EuiFlexGroup gutterSize='xs' alignItems='center' responsive={false} wrap={true}>
          {sliderNodeTypes.map((sliderNodeType, index) => (
            <Fragment key={`${sliderInstanceType}-${sliderNodeType}-${index}`}>
              {index === 0 || (
                <EuiFlexItem grow={false}>
                  <EuiText size='xs' color='subdued'>{`\u00b7`}</EuiText>
                </EuiFlexItem>
              )}

              <EuiFlexItem grow={false}>
                <EuiText size='xs' color='subdued'>
                  <FormattedMessage
                    {...getSliderPrettyName({
                      sliderInstanceType,
                      sliderNodeType,
                    })}
                  />
                </EuiText>
              </EuiFlexItem>
            </Fragment>
          ))}
        </EuiFlexGroup>

        <EuiSpacer size='xs' />
      </Fragment>
    )}

    <EuiText size='xs' color='subdued'>
      {!!totalSize &&
        getSizeOptionText({
          instanceResource: `memory`,
          storageMultiplier,
          cpuMultiplier,
          value: totalSize,
          primaryKey: `storage`,
          secondaryKey: `memory`,
          isBlobStorage: false,
          isMachineLearning: isDedicatedML({ topologyElement }),
        })}
    </EuiText>

    {diffs && dealWithDiffs(diffs)}

    <EuiSpacer size='xs' />
  </Fragment>
)

// Round to one decimal place for display
function roundToOneDecimal(value: number): number {
  return Math.round(value * 10) / 10
}

function dealWithDiffs(diffs: ZoneSummaryInstanceDiff): JSX.Element {
  // cpuDiff is already in vCPU units (calculated using getRawCpu which accounts for boosting)
  const roundedCpuDiff = roundToOneDecimal(diffs.cpuDiff)

  return (
    <EuiText>
      {diffs.totalSizeDiff !== 0 && <span>{displayDiff(diffs.totalSizeDiff, `RAM`, true)}</span>}
      {diffs.storageDiff !== 0 && <span>{displayDiff(diffs.storageDiff, `storage`, true)}</span>}
      {diffs.cpuDiff !== 0 && <span>{displayDiff(roundedCpuDiff, `vCPU`, false)}</span>}
    </EuiText>
  )
}

function displayDiff(value: number, type: string, shouldPrettySize: boolean) {
  const isNegative = value < 0
  const absoluteVal = Math.abs(value)
  const displayVal = shouldPrettySize ? prettySize(absoluteVal) : absoluteVal

  return (
    <Fragment>
      <EuiBadge color={isNegative ? 'warning' : 'success'}>
        {isNegative ? '-' : '+'}
        {displayVal} {type}
      </EuiBadge>
      &nbsp;
    </Fragment>
  )
}

export default HardwareSummary
